import { useEffect, useMemo, useRef } from 'react';
import type { UseFormGetValues, UseFormSetValue } from 'react-hook-form';
import type { ClinicEmrAgencyFlags } from '../../utils/clinicEmr';
import {
  buildSchedulingEmrSelectionPatch,
  getSchedulingEmrForClinic,
  schedulingFlagsMatchPatch,
  type IntakeSchedulingEmr,
  type SchedulingEmrFlags,
} from '../../utils/intakeSchedulingEmr';

interface AgencyClinicLike {
  id: string;
  clinic_source?: string;
  is_kinnser?: boolean;
  default_intake_emr_is_roger?: boolean;
}

type SchedulingEmrFormData = SchedulingEmrFlags &
  Record<string, string | boolean | null | undefined>;

interface UseClinicSourceSchedulingEmrOptions {
  agencyClinics: AgencyClinicLike[];
  agencyClinicId: string | null | undefined;
  isNewPatient: boolean | null | undefined;
  /** Latest persisted scheduling flags from the intake (server truth). */
  intakeSchedulingFlags: SchedulingEmrFlags;
  rogerAgencyEnabled: boolean;
  kinnserAgencyEnabled: boolean;
  scheduleRogerBlockedByMatch: boolean;
  scheduleKinnserBlockedByMatch: boolean;
  getValues: UseFormGetValues<SchedulingEmrFormData>;
  setValue: UseFormSetValue<SchedulingEmrFormData>;
  saveToServer: (data: Partial<SchedulingEmrFormData>) => void | Promise<void>;
}

interface UseClinicSourceSchedulingEmrResult {
  schedulingEmr: IntakeSchedulingEmr | null;
  showRogerSchedulingOption: boolean;
  showKinnserSchedulingOption: boolean;
  schedulingSelectionLocked: boolean;
}

function buildClinicSourceSyncKey(
  agencyClinicId: string | null | undefined,
  schedulingEmr: IntakeSchedulingEmr | null,
  isNewPatient: boolean | null | undefined
): string | null {
  if (!agencyClinicId || !schedulingEmr) return null;
  return `${agencyClinicId}:${schedulingEmr}:${String(isNewPatient)}`;
}

/**
 * Derive the scheduling EMR from the intake clinic (and Extraction EMR choice for
 * dual-capable clinics) and keep form/intake flags in sync when the clinic changes.
 */
export function useClinicSourceSchedulingEmr({
  agencyClinics,
  agencyClinicId,
  isNewPatient,
  intakeSchedulingFlags,
  rogerAgencyEnabled,
  kinnserAgencyEnabled,
  scheduleRogerBlockedByMatch,
  scheduleKinnserBlockedByMatch,
  getValues,
  setValue,
  saveToServer,
}: UseClinicSourceSchedulingEmrOptions): UseClinicSourceSchedulingEmrResult {
  const agencyFlags: ClinicEmrAgencyFlags = {
    is_kinnser_enabled: kinnserAgencyEnabled,
    is_rogeremr_enabled: rogerAgencyEnabled,
  };

  const schedulingEmr = useMemo(
    () =>
      getSchedulingEmrForClinic(agencyClinics, agencyClinicId, {
        agency: agencyFlags,
        flags: intakeSchedulingFlags,
      }),
    [
      agencyClinics,
      agencyClinicId,
      agencyFlags.is_kinnser_enabled,
      agencyFlags.is_rogeremr_enabled,
      intakeSchedulingFlags.roger_create_episode,
      intakeSchedulingFlags.kinnser_create_episode,
      intakeSchedulingFlags.push_to_roger,
      intakeSchedulingFlags.push_to_kinnser,
    ]
  );

  const showRogerSchedulingOption =
    schedulingEmr === 'roger' &&
    rogerAgencyEnabled &&
    !scheduleRogerBlockedByMatch;
  const showKinnserSchedulingOption =
    schedulingEmr === 'kinnser' &&
    kinnserAgencyEnabled &&
    !scheduleKinnserBlockedByMatch;

  // Dual-capable: locked once Extraction chose an EMR. Single-path: locked to clinic.
  const schedulingSelectionLocked = Boolean(
    showRogerSchedulingOption || showKinnserSchedulingOption
  );

  const syncKey = buildClinicSourceSyncKey(
    agencyClinicId,
    schedulingEmr,
    isNewPatient
  );

  const getValuesRef = useRef(getValues);
  getValuesRef.current = getValues;
  const setValueRef = useRef(setValue);
  setValueRef.current = setValue;
  const saveToServerRef = useRef(saveToServer);
  saveToServerRef.current = saveToServer;

  const {
    roger_create_episode: intakeRogerCreateEpisode,
    kinnser_create_episode: intakeKinnserCreateEpisode,
    push_to_roger: intakePushToRoger,
    push_to_kinnser: intakePushToKinnser,
  } = intakeSchedulingFlags;

  useEffect(() => {
    // Dual-capable without clinic id cannot resolve an EMR.
    if (!schedulingEmr || !syncKey) return;

    if (schedulingEmr === 'roger') {
      if (!rogerAgencyEnabled || scheduleRogerBlockedByMatch) return;
    } else if (!kinnserAgencyEnabled || scheduleKinnserBlockedByMatch) {
      return;
    }

    const patch = buildSchedulingEmrSelectionPatch(schedulingEmr, isNewPatient);
    const serverFlags: SchedulingEmrFlags = {
      roger_create_episode: intakeRogerCreateEpisode,
      kinnser_create_episode: intakeKinnserCreateEpisode,
      push_to_roger: intakePushToRoger,
      push_to_kinnser: intakePushToKinnser,
    };
    const formFlags = getValuesRef.current();
    const serverMatches = schedulingFlagsMatchPatch(serverFlags, patch);
    const formMatches = schedulingFlagsMatchPatch(formFlags, patch);

    if (!formMatches) {
      (
        Object.entries(patch) as Array<
          [keyof SchedulingEmrFormData, boolean | null]
        >
      ).forEach(([field, value]) => {
        setValueRef.current(field, value);
      });
    }

    // Persist when flags are empty/mismatched — including dual-capable Kinnser default
    // so Scheduling push_to_* checkboxes stay aligned with the Extraction dropdown.
    if (!serverMatches) {
      void saveToServerRef.current(patch);
    }
  }, [
    syncKey,
    schedulingEmr,
    isNewPatient,
    intakeRogerCreateEpisode,
    intakeKinnserCreateEpisode,
    intakePushToRoger,
    intakePushToKinnser,
    rogerAgencyEnabled,
    kinnserAgencyEnabled,
    scheduleRogerBlockedByMatch,
    scheduleKinnserBlockedByMatch,
  ]);

  return {
    schedulingEmr,
    showRogerSchedulingOption,
    showKinnserSchedulingOption,
    schedulingSelectionLocked,
  };
}
